import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Bug, Lightbulb, Heart, X } from "lucide-react";

interface InCanvasFeedbackProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackSubmit: (feedback: {
    type: string;
    message: string;
    context: string;
  }) => void;
}

const InCanvasFeedback = ({
  isOpen,
  onClose,
  onFeedbackSubmit,
}: InCanvasFeedbackProps) => {
  const [feedbackType, setFeedbackType] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!feedbackType || !message.trim()) return;

    setIsSubmitting(true);
    try {
      await onFeedbackSubmit({
        type: feedbackType,
        message: message.trim(),
        context: "in-canvas", // Indicates this feedback came from the canvas
      });

      // Reset form
      setFeedbackType("");
      setMessage("");
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const feedbackTypes = [
    {
      id: "bug",
      label: "Report a Bug",
      description: "Something isn't working correctly",
      icon: Bug,
      color: "text-red-500",
    },
    {
      id: "suggestion",
      label: "Feature Suggestion",
      description: "I have an idea for improvement",
      icon: Lightbulb,
      color: "text-yellow-500",
    },
    {
      id: "praise",
      label: "Love This Feature",
      description: "This is working great!",
      icon: Heart,
      color: "text-green-500",
    },
    {
      id: "general",
      label: "General Feedback",
      description: "Other thoughts or comments",
      icon: MessageCircle,
      color: "text-blue-500",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md mx-auto max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <MessageCircle className="h-5 w-5 text-primary" />
            Quick Feedback
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            Help us improve your design experience. What's on your mind?
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {/* Feedback Type */}
          <div>
            <label className="text-sm font-medium mb-3 block">
              What type of feedback?
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {feedbackTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setFeedbackType(type.id)}
                  className={`p-2 sm:p-3 rounded-lg border-2 transition-all ${
                    feedbackType === type.id
                      ? "border-primary bg-primary/10"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <type.icon
                    className={`h-4 w-4 sm:h-5 sm:w-5 ${type.color} mx-auto mb-1`}
                  />
                  <div className="text-xs sm:text-sm font-medium">
                    {type.label}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    {type.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Tell us more
            </label>
            <Textarea
              placeholder="Describe what you're experiencing or what you'd like to see..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[80px] sm:min-h-[100px] text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Fixed Action Buttons */}
        <div className="flex-shrink-0 pt-4 border-t bg-background">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 h-10 sm:h-auto"
              disabled={isSubmitting}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 h-10 sm:h-auto"
              disabled={!feedbackType || !message.trim() || isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Feedback"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InCanvasFeedback;
